import { forwardRef, type ReactNode } from 'react';
import { cn } from '../utils/cn.js';

export interface SegmentedProps<V extends string = string> {
  value: V;
  onChange: (v: V) => void;
  options: Array<{ value: V; label: ReactNode }>;
  size?: 'sm' | 'md';
  className?: string;
  ariaLabel?: string;
}

/**
 * A tab-of-buttons segmented control. Uses `aria-pressed` (not radiogroup) — the buttons
 * are independent controls sharing state; matches how Kollez pages use `.seg` in `kollez.css`.
 */
export function Segmented<V extends string>({
  value,
  onChange,
  options,
  size = 'md',
  className,
  ariaLabel,
}: SegmentedProps<V>) {
  return (
    <div
      role="group"
      aria-label={ariaLabel}
      className={cn('seg', size === 'sm' && 'seg-sm', className)}
    >
      {options.map((opt) => {
        const active = opt.value === value;
        return (
          <button
            key={opt.value}
            type="button"
            className={cn('seg-btn', active && 'is-active')}
            aria-pressed={active}
            onClick={() => onChange(opt.value)}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

Segmented.displayName = 'Segmented';
